/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 19-08-2026
 *
 * Root-service met de samenwerk-sessie-state (golf 3a): de rol van dit model in een
 * samenwerking, de werkmodus, wie er meekijkt en of er verbinding is. De acties eromheen
 * (primary worden, aanhaken, ontkoppelen) staan sinds golf 4c op de `ModelbeheerService`.
 *
 * Naast de dialoog leest de modeltitel (meekijkers), de modeltoolbar (replay-gating) en straks
 * de presence-weergave van golf 3e hieruit; golf 3b heeft de leider/volger-computeds nodig om
 * te bepalen of een gebaar van een volger nog langs de leider moet voor de actielog.
 *
 * Terminologie: de rollen heten `primary` en `sub`, ook op de wire — sinds de protocolronde b0-2
 * (08-09-2026) levert het backend ze zo. De historische termen `master`/`slave` staan alleen nog
 * in de databasekolom, die `dynalearn-admin` en `dynalearn-material` lezen; die omzetting doet het
 * backend zelf (`helpers/samenwerkrol.js`). `rolUitBackend` hieronder is daarmee een
 * identiteitsfunctie met een vangnet geworden en kan weg zodra de DB-migratieronde is geweest.
 */
import {computed, inject, Injectable, signal} from '@angular/core';
import {BsModalService} from 'ngx-bootstrap/modal';
import {ApiService} from '../app/backend/api.service';
import type {ModelId} from '../app/backend/api.service';
import {DlSamenwerkdialoogComponent} from '../components/dialogs/dl-samenwerkdialoog/dl-samenwerkdialoog.component';

/**
 * De rol van dit model in een samenwerking: alleen (`single`), het model waar de anderen
 * onder werken (`primary`), of een gekoppelde kopie daarvan (`sub`).
 */
export type SamenwerkRol = 'single' | 'primary' | 'sub';

/**
 * Neemt de rol over zoals het backend hem levert, met `single` als vangnet voor alles wat geen
 * geldige rol is. Sinds b0-2 spreekt de wire dezelfde termen als wij, dus dit is een controle en
 * geen vertaling meer; hij blijft staan omdat een onverwachte waarde (een oud backend, een
 * kolomwaarde die per ongeluk doorlekt) anders stilzwijgend als rol zou gelden.
 * @param samenwerking de waarde uit het backendantwoord
 */
export function rolUitBackend(samenwerking: string | null | undefined): SamenwerkRol {
    switch (samenwerking) {
        case 'primary':
            return 'primary';
        case 'sub':
            return 'sub';
        default:
            return 'single';
    }
}

/**
 * Hoe lokale en binnenkomende wijzigingen verwerkt worden: `lokaal` zonder samenwerking,
 * `leid` als deze client de mutaties uitvoert en rondstuurt, `volg` als hij ze aanvraagt.
 */
export type Werkmodus = 'lokaal' | 'leid' | 'volg';

/**
 * Eén deelnemer zoals het backend hem in de samenwerkstate meestuurt.
 */
export interface SamenwerkDeelnemer {
    id?: number;
    user: string;
}

/**
 * Eén aanwezige client op dit model, zoals de `clientnames`-notify hem levert. Wie terugbladert
 * gaat hier sinds golf a5 niet meer in mee (besluit 09-09-2026): dat is net zo lokaal als het open-
 * en dichtklappen van een paneel, en met de leiderwissel valt er ook niets uit te leggen.
 */
export interface SamenwerkAanwezige {
    naam: string;
}

/**
 * De samenwerkstate van het model zoals het backend hem teruggeeft, aangevuld met de eigen
 * gebruikersnaam zodat de weergave de naam van de primary kan bepalen zonder de sessie te
 * kennen.
 */
export interface SamenwerkState {
    /** De rol zoals het backend hem levert. */
    samenwerking: SamenwerkRol;
    /** De 3x3-code waarmee anderen kunnen aanhaken; alleen bij een primary. */
    primarycode?: string;
    /** Het model waar deze samenwerking onder hangt; alleen bij een sub. */
    primary?: SamenwerkDeelnemer;
    /** De aangehaakte modellen; alleen bij een primary. */
    subs?: SamenwerkDeelnemer[];
    /** De weergavenaam van de ingelogde gebruiker. */
    eigenNaam: string;
}

//de vijf `SamenwerkingActies` die AngularJS hier registreerde (de samenwerkstate, de meekijker,
//primary worden, aanhaken en ontkoppelen) zijn met golf 4c methodes van de `ModelbeheerService`
//geworden. Hier konden ze niet: de `OpslagService`, de samenwerk-baas en de `SimulatieService`
//injecteren deze service al, en de acties hebben die drie nodig

/**
 * De push-ingang die de dialoog aanbiedt zolang hij open staat.
 */
export interface SamenwerkingWeergave {
    /** Haal de samenwerkstate opnieuw op (na een notify van het backend). */
    ververs: () => void;
}

@Injectable({providedIn: 'root'})
export class SamenwerkingService {

    /**
     * De werkmodus van dit model.
     */
    private readonly _werkmodus = signal<Werkmodus>('lokaal');

    /**
     * De rol van dit model in een samenwerking.
     */
    private readonly _rol = signal<SamenwerkRol>('single');

    /**
     * De andere gebruikers die dit model op dit moment openhebben, met hun replaystand. De eigen
     * client zit er niet bij — die filtert de samenwerk-baas eruit voordat hij hem hier zet.
     */
    private readonly _aanwezigen = signal<SamenwerkAanwezige[]>([]);

    /**
     * Het model waarmee wordt meegekeken, of `null` als dit geen meekijksessie is.
     */
    private readonly _meekijkmodel = signal<ModelId | null>(null);

    /**
     * Of er verbinding is met het backend.
     */
    private readonly _verbonden = signal(false);

    /**
     * De push-ingang van de geopende dialoog, of `null` als er geen dialoog open staat.
     */
    private weergave: SamenwerkingWeergave | null = null;

    /**
     * De werkmodus van dit model.
     */
    readonly werkmodus = this._werkmodus.asReadonly();

    /**
     * De rol van dit model in een samenwerking.
     */
    readonly rol = this._rol.asReadonly();

    /**
     * De andere gebruikers die dit model openhebben, met hun replaystand.
     */
    readonly aanwezigen = this._aanwezigen.asReadonly();

    /**
     * Het model waarmee wordt meegekeken, of `null`.
     */
    readonly meekijkmodel = this._meekijkmodel.asReadonly();

    /**
     * Of er verbinding is met het backend.
     */
    readonly verbonden = this._verbonden.asReadonly();

    /**
     * Of dit model in een samenwerking zit.
     */
    readonly werkSamen = computed(() => this._rol() !== 'single');

    /**
     * Of we los van anderen werken.
     */
    readonly isLokaal = computed(() => this._werkmodus() === 'lokaal');

    /**
     * Of deze client de mutaties uitvoert en rondstuurt.
     */
    readonly isLeider = computed(() => this._werkmodus() === 'leid');

    /**
     * Of deze client zijn mutaties bij de leider aanvraagt.
     */
    readonly isVolger = computed(() => this._werkmodus() === 'volg');

    /**
     * Of er anderen naar dit model kijken.
     */
    readonly heeftMeekijkers = computed(() => this._aanwezigen().length > 0);

    /**
     * De aanwezigen als één regel, voor het oogje bij de modeltitel.
     */
    readonly clientnames = computed(() => this._aanwezigen().map(aanwezige => aanwezige.naam).join(', '));

    /**
     * Het transport, dat elke verbindingswijziging meldt.
     */
    private readonly api = inject(ApiService);

    /**
     * Voor de samenwerkdialoog.
     */
    private readonly modalService = inject(BsModalService);

    constructor() {
        this._verbonden.set(this.api.verbonden);
        this.api.onIntern().subscribe(() => this._verbonden.set(this.api.verbonden));
    }

    /* ******** De state: geschreven door de opslagketen, de samenwerk-baas en de samenwerkdialoog ******** */

    /**
     * Zet de werkmodus.
     */
    zetWerkmodus(werkmodus: Werkmodus): void {
        this._werkmodus.set(werkmodus || 'lokaal');
    }

    /**
     * Zet de rol vanuit de wire-vorm van het backend.
     */
    zetRolUitBackend(samenwerking: string | null | undefined): void {
        this._rol.set(rolUitBackend(samenwerking));
    }

    /**
     * Zet de andere gebruikers die dit model openhebben, zonder de eigen client.
     */
    zetAanwezigen(aanwezigen: SamenwerkAanwezige[]): void {
        this._aanwezigen.set(aanwezigen ?? []);
    }

    /**
     * Zet het model waarmee wordt meegekeken.
     */
    zetMeekijkmodel(meekijkmodel: ModelId | null): void {
        this._meekijkmodel.set(meekijkmodel ?? null);
    }

    /* ******** Richting de dialoog ******** */

    /**
     * Opent de samenwerkdialoog. Die haalt zijn eigen stand op bij de `ModelbeheerService`, dus er
     * is niets terug te geven.
     */
    openSamenwerkdialoog(): void {
        this.modalService.show(DlSamenwerkdialoogComponent, {
            backdrop: 'static',
            animated: true,
            class: 'dl-modal'
        });
    }

    /**
     * De dialoog meldt zich aan zolang hij open staat.
     */
    registreerWeergave(weergave: SamenwerkingWeergave): void {
        this.weergave = weergave;
    }

    /**
     * De dialoog meldt zich af bij het sluiten.
     */
    deregistreerWeergave(weergave: SamenwerkingWeergave): void {
        if (this.weergave === weergave) {
            this.weergave = null;
        }
    }

    /**
     * Er is een samenwerk-notify binnengekomen; een geopende dialoog haalt zijn state opnieuw
     * op. Zonder open dialoog gebeurt er niets.
     */
    meldVerversen(): void {
        this.weergave?.ververs();
    }
}
